import React, { useState, useEffect, useCallback } from 'react';
import { Search, MapPin, Phone, FileText, User, X, Filter } from 'lucide-react';
import apiService from '../config';
import { useLanguage } from '../StoreContext/LanguageContext';

const SearchBar = () => {
  const { language, translations } = useLanguage();
  const [surveyNo, setSurveyNo] = useState('');
  const [mobileNo, setMobileNo] = useState('');
  const [landholderName, setLandholderName] = useState('');
  const [selectedVillage, setSelectedVillage] = useState('');
  const [selectedTaluka, setSelectedTaluka] = useState('');
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [error, setError] = useState(null);

  // Project state
  const [currentProjectId, setCurrentProjectId] = useState(null);
  const [currentProjectName, setCurrentProjectName] = useState(null);
  const [proposalId, setProposalId] = useState(null);


  // Dynamic dropdown data
  const [districts, setDistricts] = useState([]);
  const [talukas, setTalukas] = useState([]);
  const [villages, setVillages] = useState([]);

  // Loading states for dropdowns
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [loadingTalukas, setLoadingTalukas] = useState(false);
  const [loadingVillages, setLoadingVillages] = useState(false);

  // Get project ID and name from localStorage
  useEffect(() => {
    const getProjectInfo = () => {
      // Try to get from current project data first
      const currentProjectData = localStorage.getItem('currentProjectData');
      if (currentProjectData) {
        try {
          const projectData = JSON.parse(currentProjectData);
          return {
            id: projectData.project_id || projectData.id,
            name: projectData.project_name || projectData.name
          };
        } catch (e) {
          console.error('Error parsing current project data:', e);
        }
      }

      // Try to get from selected project in localStorage
      const selectedProject = localStorage.getItem('selectedProject');
      if (selectedProject) {
        try {
          const project = JSON.parse(selectedProject);
          return {
            id: project.project_id || project.id,
            name: project.project_name || project.name
          };
        } catch (e) {
          console.error('Error parsing selected project:', e);
        }
      }

      // Fallback to userData
      const userData = localStorage.getItem('userData');
      if (userData) {
        try {
          const user = JSON.parse(userData);
          return {
            id: user.data?.project_id || user.project_id,
            name: user.data?.project_name || user.project_name || 'Unknown Project'
          };
        } catch (e) {
          console.error('Error parsing user data:', e);
        }
      }

      return { id: null, name: null };
    };

    const projectInfo = getProjectInfo();
    setCurrentProjectId(projectInfo.id);
    setCurrentProjectName(projectInfo.name);
    console.log('Current Project Info:', projectInfo);
  }, []);

  useEffect(() => {
    const getProposalInfo = () => {
      const currentProposal = JSON.parse(localStorage.getItem('userData') || '{}');

      if (currentProposal) {
        try {
          return (
            currentProposal?.data?.default_proposal ||
            currentProposal?.data?.proposal_id ||
            currentProposal?.default_proposal ||
            currentProposal?.proposal_id ||
            null
          );
        } catch (e) {
          console.error('Error parsing current project data (getProposalInfo):', e);
        }
      }
      return null;
    };

    const propId = getProposalInfo();
    setProposalId(propId);
    console.log('✅ Proposal ID loaded:', propId);
  }, []);


  // Load districts on component mount
  useEffect(() => {
    const fetchDistricts = async () => {
      setLoadingDistricts(true);
      try {
        const response = await apiService.getDistricts();
        setDistricts(response.data || []);
      } catch (err) {
        console.error('Error fetching districts:', err);
        setError('Failed to load districts');
      } finally {
        setLoadingDistricts(false);
      }
    };

    fetchDistricts();
  }, []);

  // Load talukas when district changes
  useEffect(() => {
    const fetchTalukas = async () => {
      if (!selectedDistrict) {
        setTalukas([]);
        setSelectedTaluka('');
        setVillages([]);
        setSelectedVillage('');
        return;
      }

      setLoadingTalukas(true);
      setSelectedTaluka('');
      setVillages([]);
      setSelectedVillage('');

      try {
        const response = await apiService.getTalukas(selectedDistrict);
        setTalukas(response.data || []);
      } catch (err) {
        console.error('Error fetching talukas:', err);
        setError('Failed to load talukas');
        setTalukas([]);
      } finally {
        setLoadingTalukas(false);
      }
    };

    fetchTalukas();
  }, [selectedDistrict]);

  // Load villages when taluka changes
  useEffect(() => {
    const fetchVillages = async () => {
      if (!selectedDistrict || !selectedTaluka) {
        setVillages([]);
        setSelectedVillage('');
        return;
      }

      setLoadingVillages(true);
      setSelectedVillage('');

      try {
        const response = await apiService.getVillages(selectedDistrict, selectedTaluka);
        setVillages(response.data || []);
      } catch (err) {
        console.error('Error fetching villages:', err);
        setError('Failed to load villages');
        setVillages([]);
      } finally {
        setLoadingVillages(false);
      }
    };

    fetchVillages();
  }, [selectedDistrict, selectedTaluka]);

  // Debounced search function
  const performSearch = useCallback(async () => {
    // Require project ID
    if (!currentProjectId) {
      setError('Project ID is required for search');
      return;
    }

    const hasSearchCriteria = surveyNo.trim() || mobileNo.trim() || landholderName.trim() ||
      selectedVillage || selectedTaluka || selectedDistrict;

    if (!hasSearchCriteria) {
      setResults([]);
      setHasSearched(false);
      setError(null);
      return;
    }

    setIsLoading(true);
    setHasSearched(true);
    setError(null);

    try {
      // const searchParams = {
      //   projectId: currentProjectId,
      //   surveyNo: surveyNo.trim(),
      //   mobileno: mobileNo.trim(),
      //   distNo: selectedDistrict,
      //   talukaNo: selectedTaluka,
      //   villageNo: selectedVillage,
      //   name: landholderName.trim()
      // };

      const searchParams = {
        projectId: currentProjectId,
        proposalId: proposalId,
      };

      if (surveyNo && surveyNo.trim()) searchParams.surveyNo = surveyNo.trim();
      if (mobileNo && mobileNo.trim()) searchParams.mobileno = mobileNo.trim();
      if (selectedDistrict) searchParams.distNo = selectedDistrict;
      if (selectedTaluka) searchParams.talukaNo = selectedTaluka;
      if (selectedVillage) searchParams.villageNo = selectedVillage;
      if (landholderName && landholderName.trim()) searchParams.name = landholderName.trim();

      console.log('Search Payload:', searchParams); // Debug log



      // Remove empty parameters to avoid sending unnecessary data
      const cleanedParams = Object.fromEntries(
        Object.entries(searchParams).filter(([_, value]) => value !== undefined && value !== null && value !== '')
      );


      const response = await apiService.getSearch(cleanedParams);

      // Assuming the API returns data in response.data
      setResults(response.data || []);

    } catch (err) {
      console.error('Search error:', err);
      setError('Failed to fetch search results. Please try again.');
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  }, [surveyNo, mobileNo, landholderName, selectedVillage, selectedTaluka, selectedDistrict, currentProjectId, proposalId]);

  // Auto-search effect with debouncing
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      performSearch();
    }, 300); // 300ms debounce delay

    return () => clearTimeout(debounceTimer);
  }, [performSearch]);

  const resetSearch = () => {
    setSurveyNo('');
    setMobileNo('');
    setLandholderName('');
    setSelectedVillage('');
    setSelectedTaluka('');
    setSelectedDistrict('');
    setResults([]);
    setHasSearched(false);
    setError(null);
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (surveyNo.trim()) count++;
    if (mobileNo.trim()) count++;
    if (landholderName.trim()) count++;
    if (selectedVillage) count++;
    if (selectedTaluka) count++;
    if (selectedDistrict) count++;
    return count;
  };

  const getStageColor = (stage) => {
    switch (stage) {
      case 'Survey Completed':
        return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'Verification Complete':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Documentation Pending':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'Under Review':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Show project error if no project ID
  if (!currentProjectId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
        <div className="max-w-7xl mx-auto p-6">
          <div className="bg-red-50 border border-red-200 rounded-2xl p-8 text-center">
            <X className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-red-800 font-medium text-lg mb-2">{translations[language]?.projectRequired}</h3>
            <p className="text-red-700">{translations[language]?.selectProjectSearch}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        {/* <div className="mb-8">
          <h1 className="text-3xl font-bold text-black mb-3">
            {translations[language]?.landRecordsSearch}
          </h1>
          <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 mt-1">
            <p className="text-gray-600 text-lg">{translations[language]?.landRecordsSearchDesc}</p>
            {currentProjectName && (
              <p className="text-sm text-gray-700 font-medium">
                {translations[language]?.project}: {currentProjectName}
              </p>
            )}
            {currentProjectId && (
              <p className="text-xs text-gray-500">
                {translations[language]?.id}: {currentProjectId}
              </p>
            )}
          </div>
        </div> */}

        {/* Enhanced Search Section */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden mb-6">
          {/* Search Header */}
          <div className="bg-gradient-to-r from-blue-500 to-indigo-600 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Search className="w-6 h-6 text-white" />
                <h2 className="text-xl font-semibold text-white">{translations[language]?.searchFilters}</h2>
                {getActiveFiltersCount() > 0 && (
                  <span className="bg-white text-blue-600 px-3 py-1 rounded-full text-sm font-medium">
                    {getActiveFiltersCount()} {translations[language]?.active}
                  </span>
                )}
              </div>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="lg:hidden flex items-center gap-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg text-white transition-colors"
              >
                <Filter className="w-4 h-4" />
                {translations[language]?.filters}
              </button>
            </div>
          </div>

          {/* Search Fields */}
          <div className={`p-6 ${showFilters ? 'block' : 'hidden lg:block'}`}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {/* District */}
              <div className="relative">
                <label className="flex items-center text-sm font-medium text-gray-700 mb-3">
                  <MapPin className="w-4 h-4 mr-2 text-red-500" />
                  {translations[language]?.district}
                </label>
                <div className="relative">
                  <select
                    value={selectedDistrict}
                    onChange={(e) => setSelectedDistrict(e.target.value)}
                    disabled={loadingDistricts}
                    className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 bg-gray-50 focus:bg-white transition-colors appearance-none disabled:opacity-50"
                  >
                    <option value="">
                      {loadingDistricts ? <span>{translations[language]?.loadingDistricts}</span> : <span>{translations[language]?.selectDistrict}</span>}
                    </option>
                    {districts.map((district) => (
                      <option key={district.dist_name} value={district.dist_name}>
                        {district.dist_name}
                      </option>
                    ))}
                  </select>
                  {selectedDistrict && (
                    <button
                      onClick={() => setSelectedDistrict('')}
                      className="absolute right-8 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Taluka */}
              <div className="relative">
                <label className="flex items-center text-sm font-medium text-gray-700 mb-3">
                  <MapPin className="w-4 h-4 mr-2 text-orange-500" />
                  {translations[language]?.taluka}
                </label>
                <div className="relative">
                  <select
                    value={selectedTaluka}
                    onChange={(e) => setSelectedTaluka(e.target.value)}
                    disabled={!selectedDistrict || loadingTalukas}
                    className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 bg-gray-50 focus:bg-white transition-colors appearance-none disabled:opacity-50"
                  >
                    <option value="">
                      {!selectedDistrict
                        ? <span>{translations[language]?.selectDistrictFirst}</span>
                        : loadingTalukas
                          ? <span>{translations[language]?.loadingTalukas}</span> : <span>{translations[language]?.selectTaluka}</span>}

                    </option>
                    {talukas.map((taluka) => (
                      <option key={taluka.taluka_name} value={taluka.taluka_name}>
                        {taluka.taluka_name}
                      </option>
                    ))}
                  </select>
                  {selectedTaluka && (
                    <button
                      onClick={() => setSelectedTaluka('')}
                      className="absolute right-8 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Village */}
              <div className="relative">
                <label className="flex items-center text-sm font-medium text-gray-700 mb-3">
                  <MapPin className="w-4 h-4 mr-2 text-green-500" />
                  {translations[language]?.village}
                </label>
                <div className="relative">
                  <select
                    value={selectedVillage}
                    onChange={(e) => setSelectedVillage(e.target.value)}
                    disabled={!selectedDistrict || !selectedTaluka || loadingVillages}
                    className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 bg-gray-50 focus:bg-white transition-colors appearance-none disabled:opacity-50"
                  >
                    <option value="">
                      {!selectedDistrict || !selectedTaluka
                        ? <span>{translations[language]?.selectDistrictTalukaFirst}</span>
                        : loadingVillages
                          ? <span>{translations[language]?.loadingVillages}</span>
                          : <span>{translations[language]?.selectVillage}</span>
                      }
                    </option>
                    {villages.map((village) => (
                      <option key={village.village_name} value={village.village_name}>
                        {village.village_name}
                      </option>
                    ))}
                  </select>
                  {selectedVillage && (
                    <button
                      onClick={() => setSelectedVillage('')}
                      className="absolute right-8 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
              {/* Landholder Name */}
              <div className="relative">
                <label className="flex items-center text-sm font-medium text-gray-700 mb-3">
                  <User className="w-4 h-4 mr-2 text-blue-500" />
                  {translations[language]?.name}
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={landholderName}
                    onChange={(e) => setLandholderName(e.target.value)}
                    placeholder={translations[language]?.enterName}
                    className="w-full px-4 py-3 pl-4 pr-10 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 placeholder-gray-400 bg-gray-50 focus:bg-white transition-colors"
                  />
                  {landholderName && (
                    <button
                      onClick={() => setLandholderName('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Mobile No */}
              <div className="relative">
                <label className="flex items-center text-sm font-medium text-gray-700 mb-3">
                  <Phone className="w-4 h-4 mr-2 text-green-500" />
                  {translations[language]?.mobileNumber}
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={mobileNo}
                    onChange={(e) => setMobileNo(e.target.value)}
                    placeholder={translations[language]?.enterMobileNumber}
                    className="w-full px-4 py-3 pl-4 pr-10 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 placeholder-gray-400 bg-gray-50 focus:bg-white transition-colors"
                  />
                  {mobileNo && (
                    <button
                      onClick={() => setMobileNo('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Survey No/Gat No */}
              <div className="relative">
                <label className="flex items-center text-sm font-medium text-gray-700 mb-3">
                  <FileText className="w-4 h-4 mr-2 text-purple-500" />
                  {translations[language]?.surveyGatNumber}
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={surveyNo}
                    onChange={(e) => setSurveyNo(e.target.value)}
                    placeholder={translations[language]?.enterSurveyGatNumber}
                    className="w-full px-4 py-3 pl-4 pr-10 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 placeholder-gray-400 bg-gray-50 focus:bg-white transition-colors"
                  />
                  {surveyNo && (
                    <button
                      onClick={() => setSurveyNo('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>


            </div>

            {/* Action Buttons */}
            <div className="flex gap-4 items-center justify-between mt-6 pt-6 border-t border-gray-100">
              <div className="flex gap-3">
                <button
                  onClick={resetSearch}
                  className="flex items-center gap-2 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-colors"
                >
                  <X className="w-4 h-4" />
                  {translations[language]?.clearAll}
                </button>
              </div>

              {isLoading && (
                <div className="flex items-center gap-3 text-blue-600">
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-600 border-t-transparent"></div>
                  <span className="text-sm font-medium">{translations[language]?.searchingRecords}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-6 mb-6">
            <div className="flex items-center gap-3">
              <X className="w-5 h-5 text-red-500" />
              <div>
                <h3 className="text-red-800 font-medium">{translations[language]?.searchError}</h3>
                <p className="text-red-700 text-sm mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Results Section */}
        {results.length > 0 && (
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            <div className="bg-gradient-to-r from-emerald-500 to-teal-600 px-6 py-4">
              <h2 className="text-xl font-semibold text-white">
                {translations[language]?.searchResults}({results.length} {translations[language]?.Found})
              </h2>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {translations[language]?.surveyNo}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {translations[language]?.landholderName}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {translations[language]?.status}
                    </th>
                    {/* <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {translations[language]?.date}
                    </th> */}
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {translations[language]?.mobileNo}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {translations[language]?.location}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {results.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-lg text-xs font-medium">
                          {item.surveyNo || item.survey_no || ''}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                        {item.owner_name || item.name || item.landholder_name || ''}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full border bg-yellow-300`}>
                          {item.stage_name || item.status || 'N/A'}
                        </span>
                      </td>
                      {/* <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.cre_usr_dt ? new Date(item.cre_usr_dt).toLocaleDateString('en-IN') : 'N/A'}
                      </td> */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-mono">
                        {item.owner_contact || item.mobile_no || item.mobileno || ''}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <MapPin className="w-4 h-4 mr-2 text-gray-400" />
                          <div className="flex flex-col">
                            {/* <span className="text-xs text-gray-500">
                              {item.district || item.dist_name || ''}
                              {item.district && item.taluka_name ? ', ' : ''}
                              {item.taluka || item.talukaNo || ''}
                            </span> */}
                            <span className="font-medium">
                              {item.village || item.village_name || ''}, {item.taluka_name || ''}, {item.dist_name || ''}
                            </span>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* No Results */}
        {hasSearched && results.length === 0 && !isLoading && !error && (
          <div className="text-center py-16 bg-white rounded-2xl shadow-xl border border-gray-100">
            <div className="text-gray-300 mb-6">
              <Search className="w-16 h-16 mx-auto" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">{translations[language]?.noRecordsFound}</h3>
            <p className="text-gray-600 mb-6">{translations[language]?.tryAdjustingSearch}</p>
            <button
              onClick={resetSearch}
              className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-xl font-medium transition-colors"
            >
              {translations[language]?.clearSearch}
            </button>
          </div>
        )}

        {/* Initial State */}
        {!hasSearched && results.length === 0 && !isLoading && (
          <div className="text-center py-16 bg-white rounded-2xl shadow-xl border border-gray-100">
            <div className="text-gray-300 mb-6">
              <Search className="w-16 h-16 mx-auto" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">{translations[language]?.readyToSearch}</h3>
            <p className="text-gray-600 mb-6">{translations[language]?.enterCriteria}</p>
            <div className="flex flex-wrap justify-center gap-2 text-sm text-gray-500">
              <span className="bg-gray-100 px-3 py-1 rounded-lg">{translations[language]?.name}</span>
              <span className="bg-gray-100 px-3 py-1 rounded-lg">{translations[language]?.mobileNumber}</span>
              <span className="bg-gray-100 px-3 py-1 rounded-lg">{translations[language]?.surveyNo}</span>
              <span className="bg-gray-100 px-3 py-1 rounded-lg">{translations[language]?.district}</span>
              <span className="bg-gray-100 px-3 py-1 rounded-lg">{translations[language]?.taluka}</span>
              <span className="bg-gray-100 px-3 py-1 rounded-lg">{translations[language]?.village}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchBar;